// Editable photo grid for Asset detail. Mirrors PhotoStrip's read view but
// adds an "Add" tile (file picker, multi-select) and a delete affordance on
// each thumb. Stores S3 URLs directly on asset.photos via PATCH — no
// pms/attachments join because Asset photos are a JSONB string list.
import { useTranslation } from 'react-i18next'
import { useRef, useState, type DragEvent } from 'react'
import { Loader2, Plus, X } from 'lucide-react'
import { toast } from 'sonner'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { uploadFileOnly } from '@/services/upload-service'
import type { CompressOptions } from '@/utils/imageCompressor'
import { cn } from '@/lib/utils'

interface AssetPhotoEditorProps {
  photos: string[]
  disabled?: boolean
  thumbSize?: number
  onChange: (next: string[]) => Promise<void>
  /** 照片体系改造(走查 §一):uploadFileOnly 上传即 commit 出一条(未关联的)
   *  attachment 行,这里把行 id 回传给调用方,供建资产成功后按 createdIds
   *  补 (asset, asset_photo) entity link——否则这些行是孤儿(302/322 教训)。 */
  onUploadedAttachment?: (url: string, attachmentId: string) => void
  /** 可以把图片拖进来（建产品抽屉：只当产品照片，不识别）。默认关。 */
  droppable?: boolean
  /** 压缩档（建产品：实物照片 720 / 截图 1568）。不给 = 全站默认 */
  compress?: CompressOptions
  /** 上传开始 / 结束（建产品抽屉：传图期间不让建） */
  onBusyChange?: (busy: boolean) => void
}

export function AssetPhotoEditor({
  photos,
  disabled = false,
  thumbSize = 96,
  onChange,
  onUploadedAttachment,
  droppable = false,
  compress,
  onBusyChange,
}: AssetPhotoEditorProps) {
  const { t } = useTranslation('assets')
  const fileRef = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const [open, setOpen] = useState<string | null>(null)
  const [dragOver, setDragOver] = useState(false)

  const handlePick = () => {
    if (busy || disabled) return
    fileRef.current?.click()
  }

  const handleFiles = async (input: FileList | File[] | null) => {
    const files = input ? Array.from(input) : []
    if (files.length === 0) return
    setBusy(true)
    onBusyChange?.(true)
    try {
      const uploaded: string[] = []
      const failed: string[] = []
      for (const file of files) {
        const res = await uploadFileOnly(file, compress)
        if (res.success && res.publicUrl) {
          uploaded.push(res.publicUrl)
          if (res.attachmentId) onUploadedAttachment?.(res.publicUrl, String(res.attachmentId))
        } else {
          failed.push(file.name)
        }
      }
      if (failed.length > 0) {
        toast.error(t('photoEditor.toast.uploadFailed', { files: failed.join(', ') }))
      }
      if (uploaded.length === 0) return
      await onChange([...photos, ...uploaded])
      toast.success(t('photoEditor.toast.added', { count: uploaded.length }))
    } catch (err: any) {
      toast.error(err?.message || t('photoEditor.toast.uploadFailedGeneric'))
    } finally {
      setBusy(false)
      onBusyChange?.(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  const handleDelete = async (url: string) => {
    if (busy || disabled) return
    if (!confirm(t('photoEditor.confirmRemove'))) return
    setBusy(true)
    try {
      await onChange(photos.filter(p => p !== url))
      toast.success(t('photoEditor.toast.removed'))
    } catch (err: any) {
      toast.error(err?.message || t('photoEditor.toast.removeFailed'))
    } finally {
      setBusy(false)
    }
  }

  const dropProps = droppable && !disabled ? {
    onDragOver: (e: DragEvent) => { e.preventDefault(); setDragOver(true) },
    onDragLeave: () => setDragOver(false),
    onDrop: (e: DragEvent) => {
      e.preventDefault()
      setDragOver(false)
      if (busy) return
      void handleFiles(Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/')))
    },
  } : {}

  return (
    <>
      <div
        className={cn('flex flex-wrap gap-2', droppable && 'rounded-lg p-1', dragOver && 'bg-accent ring-2 ring-ring')}
        data-testid={droppable ? 'photo-drop-zone' : undefined}
        {...dropProps}
      >
        {photos.map((url, i) => (
          <div
            key={`${url}-${i}`}
            className="group relative shrink-0 overflow-hidden rounded-lg border bg-muted"
            style={{ width: thumbSize, height: thumbSize }}
          >
            <button
              type="button"
              onClick={() => setOpen(url)}
              className="h-full w-full"
            >
              <img
                src={url}
                alt=""
                className="h-full w-full object-cover"
                loading="lazy"
              />
            </button>
            {!disabled && (
              <button
                type="button"
                onClick={() => handleDelete(url)}
                disabled={busy}
                title={t('photoEditor.removeTitle')}
                className={cn(
                  'absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full',
                  'bg-black/60 text-white opacity-0 transition-opacity',
                  'group-hover:opacity-100 hover:bg-black/80 disabled:cursor-not-allowed',
                )}
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
        ))}

        {!disabled && (
          <button
            type="button"
            onClick={handlePick}
            disabled={busy}
            className={cn(
              'flex shrink-0 flex-col items-center justify-center gap-1 rounded-lg border border-dashed bg-muted/30',
              'text-xs text-muted-foreground hover:bg-muted hover:text-foreground',
              'disabled:cursor-not-allowed disabled:opacity-50',
            )}
            style={{ width: thumbSize, height: thumbSize }}
          >
            {busy ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <>
                <Plus className="h-5 w-5" />
                <span>{t('photoEditor.addLabel')}</span>
              </>
            )}
          </button>
        )}
      </div>

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />

      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="max-w-3xl p-2">
          {open && <img src={open} alt="" className="max-h-[80vh] w-full object-contain" />}
        </DialogContent>
      </Dialog>
    </>
  )
}
